import { Button } from "@/components/button";
import CalendarDateRangePicker from "@/components/data-range-picker";
import { Label } from "@/components/label";
import QuestionMarkTooltip from "@/components/questionmark-tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/select";
import { Separator } from "@/components/separator";
import { ToggleGroup, ToggleGroupItem } from "@/components/toggle-group";

import { useAudienceAndCampaignOptions, useCalibrationPlots } from "@/http/api-hooks";
import { CampaignType, CampaignTypeValuesInstallOnly } from "@/http/api-types";
import { useUrlParams } from "@/http/use-url-params";
import { campaignTypeLabels } from "@/lib/mappings";
import { useFilterTracking } from "@/lib/mixpanel";
import { formatDateToYYYYMMDD, getAudienceSelectOptions, getCampaignSelectOptions } from "@/lib/utils";
import CalibrationPlots from "./calibration-plots";

function CalibrationPlotsPage() {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 2); // Default date

  const { paramState, updateParam, updateDateRange } = useUrlParams({
    audience: {
      name: "audience",
      type: "string",
      defaultValue: "",
    },
    campaign: {
      name: "campaign",
      type: "string",
      defaultValue: "",
    },
    campaignType: {
      name: "campaign_type",
      type: "enum",
      validValues: CampaignTypeValuesInstallOnly as readonly string[],
      defaultValue: "all" as CampaignType,
    },
    fromDate: {
      name: "from",
      type: "date",
      defaultValue: yesterday,
    },
    toDate: {
      name: "to",
      type: "date",
      defaultValue: yesterday,
    },
  });

  const currentDate = {
    from: paramState.fromDate as Date,
    to: paramState.toDate as Date,
  };

  const { data: audienceCampaignData, isLoading: audienceOptionsLoading } = useAudienceAndCampaignOptions({
    campaign_type: paramState.campaignType as string,
  });

  const audienceOptions = getAudienceSelectOptions(audienceCampaignData);

  const { trackFilterChange } = useFilterTracking();

  const onAudienceSelectChange = (value: string) => {
    trackFilterChange("audience", value);
    updateParam("campaign", "");
    updateParam("audience", value);
  };

  const onCampaignSelectChange = (value: string) => {
    trackFilterChange("campaign", value);
    updateParam("campaign", value);
  };

  const onCampaignTypeChange = (value: string) => {
    trackFilterChange("campaign_type", value);
    updateParam("audience", "");
    updateParam("campaign", "");
    updateParam("campaignType", value);
  };

  const campaignOptions = getCampaignSelectOptions(audienceOptions, paramState.audience);

  const { trigger, isMutating, data, error } = useCalibrationPlots({
    start_date: currentDate ? formatDateToYYYYMMDD(currentDate.from) : undefined,
    end_date: currentDate ? formatDateToYYYYMMDD(currentDate.to) : undefined,
    audience: parseInt(paramState.audience),
    campaign: parseInt(paramState.campaign),
    campaign_type: paramState.campaignType as string,
  });

  const onRunAnalysis = () => {
    trigger();
  };

  const showAllAudiencesOption = paramState.campaignType === "ctv_install";

  return (
    <>
      <div className="flex flex-col gap-4 mb-8">
        <div>
          <ToggleGroup
            type="single"
            value={paramState.campaignType as string}
            onValueChange={onCampaignTypeChange}
            variant="outline"
          >
            <ToggleGroupItem value={"all"} className="min-w-[100px]">
              {campaignTypeLabels.all}
            </ToggleGroupItem>
            <ToggleGroupItem value={"install"} className="min-w-[100px]">
              {campaignTypeLabels.install}
            </ToggleGroupItem>
            <ToggleGroupItem value={"ctv_install"} className="min-w-[100px]">
              {campaignTypeLabels.ctv_install}
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        <div className="flex justify-start space-x-8 items-end">
          <div className="grid grid-cols-[repeat(4,260px)] gap-4">
            <div>
              <Label className="font-semibold">Date</Label>
              <CalendarDateRangePicker
                date={currentDate}
                setDate={newDate => {
                  if (newDate) {
                    trackFilterChange("date_range", { from: newDate.from, to: newDate.to });
                  }
                  updateDateRange(newDate, "fromDate", "toDate");
                }}
              />
            </div>
            <div>
              <Label className="font-semibold">Audience</Label>
              <Select
                value={paramState.audience as string}
                onValueChange={onAudienceSelectChange}
                disabled={audienceOptionsLoading}
              >
                <SelectTrigger value={paramState.audience as string} onReset={() => updateParam("audience", "")}>
                  <SelectValue placeholder="Select audiences" />
                </SelectTrigger>
                <SelectContent>
                  {showAllAudiencesOption && <SelectItem value={"0"}>All Audiences</SelectItem>}
                  {audienceOptions &&
                    Object.keys(audienceOptions).map(option => (
                      <SelectItem key={option} value={option}>
                        {option + " - " + audienceOptions[option].audience_name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-semibold">
                Campaign <QuestionMarkTooltip content="Select a specific campaign or leave empty for all campaigns." />
              </Label>
              <Select
                value={paramState.campaign as string}
                onValueChange={onCampaignSelectChange}
                disabled={audienceOptionsLoading}
              >
                <SelectTrigger value={paramState.campaign as string} onReset={() => updateParam("campaign", "")}>
                  <SelectValue placeholder="Select campaigns" />
                </SelectTrigger>
                <SelectContent>
                  {audienceOptions &&
                    campaignOptions.map(option => (
                      <SelectItem key={option.id} value={option.id.toString()}>
                        {option.id + " - " + option.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end align-baseline">
              <Button onClick={onRunAnalysis}>Run Analysis</Button>
            </div>
          </div>
        </div>
      </div>
      <Separator className="my-12" />
      <div className="grid gap-4">
        <CalibrationPlots data={data} error={error} isMutating={isMutating} />
      </div>
    </>
  );
}

export default CalibrationPlotsPage;
